import React, { useContext } from 'react'
import SilageStepLayout from '../SilageStepLayout'
import { Checkbox, Form } from 'semantic-ui-react'
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { HarvestingWrapper, StyledDivider, StyledGroup } from './style';
import { FormFields } from '~/components/FormFields';
import InfoPopup from '~/components/InfoPopup';
import { ClimaOptionsContext } from '.';
import { GRASS_SILAGE_ID } from '../..';
import HarvestSlider from './HarvestSlider';
import LocationTableTooltip from '~/pages/Farmer/ClimateCheck/AnswerClimateCheck/Steps/ForageProdStep/GrassSilage/LocationTableTooltip';
import { handleErrors } from '~/utils/helpers';
import CO2Service from '~/api/services/co2';
import { useParams } from 'react-router-dom';

const FirstGrassStep: React.FC = () => {
  const { id } = useParams()
  const { control, setValue, clearErrors } = useFormContext()

  const {
    locationFavorabilityTypes,
    fertilizationTypes,
    pruningPhases,
    fieldSizes,
    silageTypes,
    cuttingFrequencies,
    setIsLoadingResults,
    setWorkOperations
  } = useContext(ClimaOptionsContext)

  const watchHarvesting = useWatch({ control, name: 'grassSilage.harvestingMethods' }) || []
  const watchPruningnPhaseId = useWatch({ control, name: 'grassSilage.pruningPhaseId' })
  const watchAvgFieldSize = useWatch({ control, name: 'grassSilage.averageFieldSize' })
  const watchFertilisation = useWatch({ control, name: 'grassSilage.fertilizationType' })

  const harvestingOptions = silageTypes?.find(silageType => silageType.id === GRASS_SILAGE_ID)?.harvestingMethods || []

  async function getPredefinedData(idMain: string) {
    setIsLoadingResults(true)
    try {
      const response = await CO2Service.getGrassSilagePredefinedData(id, { main: idMain })
      setWorkOperations(response.data.workOperations)
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsLoadingResults(false)
    }

  }

  function handleChecked(harvestingOption) {
    if (watchHarvesting.length === 0) {
      getPredefinedData(harvestingOption.id)
    }
    return [...watchHarvesting, { ...harvestingOption, percent: watchHarvesting.length === 0 ? '100' : 0 }]
  }

  function handleUnchecked(harvestingOption, indexChecked) {
    //deselection of secundary harvest
    if (watchHarvesting?.length > 0 && indexChecked > 0) {
      const tempWatchHarvest = [...watchHarvesting]
      tempWatchHarvest[0].percent = 100
      return tempWatchHarvest.filter(harvest => harvest.id !== harvestingOption.id)
    }

    //deselection of primary harvest
    return []
  }

  return <SilageStepLayout
    infoText={`Die Abfrage zur Grassilagebewirtschaftung bezieht sich auf eine durchschnittliche Grünlandfläche Ihres Betriebs.
       Bitte berücksichtigen Sie dies bei der Eingabe Ihrer Daten. Falls sich Bewirtschaftung oder Erträge auf mehreren Flächen unterscheiden, 
       bilden Sie geeignete Mittelwerte, um eine repräsentative Angabe zu ermöglichen.
       Die Ernte- und Ertragsparameter werden benötigt, um den Ertrag schätzen zu können. `}
  >
    <StyledGroup widths={'equal'}>
      <Controller
        control={control}
        name='grassSilage.harvestingMethods'
        rules={{
          required: true
        }}
        render={({ fieldState: { error } }) => (
          <HarvestingWrapper error={Boolean(error)}>
            <label className='title' style={{ width: 'fit-content' }}>
              <div>{'Ernteverfahren'}</div>
              <StyledDivider />
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {harvestingOptions.map(harvestingOption => {

                const indexChecked = watchHarvesting.findIndex(harvesting => harvesting.id === harvestingOption.id)
                const harvestChecked = indexChecked >= 0
                const singleHarvestSelected = watchHarvesting.length === 1
                const maxHarvestSelected = watchHarvesting.length === 2
                const displayProcent = maxHarvestSelected && harvestChecked
                const displaySliderLabels = maxHarvestSelected && harvestChecked && watchHarvesting[1].id === harvestingOption.id

                return <Form.Field>
                  <Checkbox
                    key={harvestingOption.id}
                    style={{ marginBottom: 3 }}
                    label={displayProcent ? `${harvestingOption.label} (${watchHarvesting[indexChecked]?.percent}%)` : harvestingOption.label}
                    className={singleHarvestSelected && !harvestChecked ? 'disable-harvest-method' : ''}
                    disabled={maxHarvestSelected && !harvestChecked}
                    checked={harvestChecked}
                    onChange={(_, data) => {
                      const harvestingOptions = data.checked ?
                        handleChecked(harvestingOption) :
                        handleUnchecked(harvestingOption, indexChecked)

                      setValue('grassSilage.harvestingMethods', harvestingOptions)
                      if (watchHarvesting?.length > 0) {
                        clearErrors('grassSilage.harvestingMethods')
                      }
                    }}
                  />
                  {displaySliderLabels && <HarvestSlider
                    primaryHarvestKey='grassSilage.harvestingMethods[0].percent'
                    secondaryHarvestKey='grassSilage.harvestingMethods[1].percent'
                  />}
                </Form.Field>
              })}
            </div>
          </HarvestingWrapper>
        )}
      />
      <Controller
        control={control}
        name='grassSilage.pruningPhaseId'
        render={({ fieldState: { error } }) => (
          <Form.Field error={Boolean(error)}>
            <label className='title' style={{ width: 'fit-content' }}>
              <div>{'Schnittzeitpunkt'}</div>
              <StyledDivider />
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {pruningPhases?.map(prunningPhase => {
                return <Checkbox
                  radio
                  key={prunningPhase.id}
                  label={prunningPhase.label}
                  checked={watchPruningnPhaseId === prunningPhase.id}
                  onClick={() => {
                    setValue('grassSilage.pruningPhaseId', prunningPhase.id)
                    clearErrors('grassSilage.pruningPhaseId')
                  }}
                />
              })}
            </div>
          </Form.Field>
        )}
      />
    </StyledGroup>

    <StyledGroup widths={'equal'}>
      <FormFields.Input
        control={control}
        rules={{
          required: true
        }}
        withoutAsterisk
        noErrorMessage
        typeInput={'controled'}
        label={'Anbaufläche'}
        suffix=' ha'
        placeholder={'in ha'}
        name={'grassSilage.areaUnderCultivation'}
      />
      <Controller
        control={control}
        name='grassSilage.averageFieldSize'
        render={({ fieldState: { error } }) => (
          <Form.Field error={Boolean(error)}>
            <label className='title' style={{ width: 'fit-content' }}>
              <div>{'Durchschnittliche Schlaggröße'}</div>
              <StyledDivider />
            </label>
            <div style={{ display: 'flex', gap: 10 }}>
              {fieldSizes.map(field => {
                return <Checkbox
                  radio
                  key={field.key}
                  label={field.label}
                  checked={watchAvgFieldSize === field.key}
                  onClick={() => {
                    setValue('grassSilage.averageFieldSize', field.key)
                    clearErrors('grassSilage.averageFieldSize')
                  }}
                />
              })}
            </div>
          </Form.Field>
        )}
      />
    </StyledGroup>

    <StyledGroup widths={'equal'}>
      <Form.Field>
        <FormFields.Select
          label={<label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div>{'Standort'}</div>
            <InfoPopup
              content={<LocationTableTooltip />}
              position={'top center'}
              hoverable={true}
              backgroundTransparent
              styleIcon={{ fontSize: '1.2em' }}
            />
          </label>}
          control={control}
          onChange={(e, data) => {
            setValue('grassSilage.locationFavorability', data.value)
            clearErrors('grassSilage.locationFavorability')
          }}
          placeholder='Anzahl Standort'
          name='grassSilage.locationFavorability'
          options={locationFavorabilityTypes.map(type => ({ value: type.key, text: type.label }))}
          noErrorMessage
          withoutAsterisk
        />

        <FormFields.Select
          label={'Schnitthäufigkeit'}
          className={'marginField'}
          control={control}
          scrolling
          upward
          placeholder='Anzahl Schnitthäufigkeit'
          name='grassSilage.cuttingFrequency'
          onChange={(e, data) => {
            setValue('grassSilage.cuttingFrequency', data.value)
            clearErrors('grassSilage.cuttingFrequency')
          }}
          options={cuttingFrequencies.map(type => ({ value: type.key, text: type.label }))}
          noErrorMessage
          withoutAsterisk
        />
      </Form.Field>

      <Form.Field>
        <Controller
          name='grassSilage.fertilizationType'
          render={({ fieldState: { error } }) => (
            <Form.Field error={Boolean(error)} id={'grassSilage.fertilizationType'}>
              <div style={{ display: 'flex', gap: 20, marginTop: 32 }}>
                {fertilizationTypes.map(fertType => {
                  return <Checkbox
                    radio
                    key={fertType.key}
                    label={fertType.label}
                    checked={watchFertilisation === fertType.key}
                    onClick={() => {
                      setValue('grassSilage.fertilizationType', fertType.key)
                      clearErrors('grassSilage.fertilizationType')
                    }}
                  />
                })}
              </div>
            </Form.Field>
          )}
        />
        <FormFields.Input
          style={{ marginTop: 31 }}
          label={'Wie ist der Trockensubstanz-Gehalt der Grassilage?'}
          name='grassSilage.dryMatter'
          suffix=' % TM'
          placeholder='in % TM'
          noErrorMessage
          withoutAsterisk
          onChange={(event) => {
            setValue('grassSilage.dryMatter', event?.target?.value.replace('% TM', ''))
            clearErrors('grassSilage.dryMatter')
          }}
          control={control}
          typeInput='controled'
        />
      </Form.Field>
    </StyledGroup>

    <StyledGroup widths={'equal'} style={{ alignItems: 'end' }}>

      <FormFields.Input
        control={control}
        rules={{
          required: true
        }}
        withoutAsterisk
        noErrorMessage
        typeInput={'controled'}
        label={'Wie hoch ist der Frischmasse-Ertrag der Grassilage nach Abzug der Ernteverluste?'}
        decimalScale={2}
        allowDecimals
        suffix=' t FM/ha'
        placeholder={'in t FM/ha'}
        name={'grassSilage.freshMassYield'}
      />

      <FormFields.Input
        control={control}
        rules={{
          required: true
        }}
        withoutAsterisk
        noErrorMessage
        decimalScale={2}
        allowDecimals
        suffix=' MJ NEL/kg TM'
        typeInput={'controled'}
        label={'Energiegehalt der Silage'}
        placeholder={'in MJ NEL/kg TM'}
        name={'grassSilage.energy'}
      />

      <FormFields.Input
        control={control}
        rules={{
          required: true
        }}
        withoutAsterisk
        decimalScale={2}
        allowDecimals
        noErrorMessage
        suffix=' g XP/kg TM'
        typeInput={'controled'}
        label={'Rohproteingehalt der Silage'}
        placeholder={'in g XP/kg TM'}
        name={'grassSilage.rawProtein'}
      />
    </StyledGroup>
  </SilageStepLayout>
}
export default FirstGrassStep